/**
 * 画布链式执行引擎
 *
 * 两种入口：
 *  - executeChain：单链路执行（节点内「运行链路」按钮）
 *    1. 从目标节点向上游回溯，收集整条链路上的所有节点
 *    2. 按拓扑排序（从上游到下游）依次执行
 *    3. 跳过已有输出的 LLM 节点（避免重复执行）
 *    4. 任一节点失败时中断链路并抛出错误
 *  - executeAllNodes：全画布执行（工具条「执行全部」按钮）
 *    按全画布拓扑序串行执行所有可执行节点，单节点失败不中断其他分支
 */
import { boardActions, getBoardState } from './state/boardStore';
import {
  aggregateUpstream,
  aggregateUpstreamVideoRefs,
  waitForUpstreamIdle,
} from './state/upstream';
import { runLlmGeneration } from './generators/llmGenerator';
import { runImageGeneration } from './generators/imageGenerator';
import { runVideoGeneration } from './generators/videoGenerator';
import { modelSupportsVideoReference } from './modelCache';
import type { CanvasEdge, CanvasNode, GenerateVideoNodeData } from './types';

/**
 * 递归收集目标节点的所有上游依赖节点 ID（去重）
 */
export function collectUpstreamChain(
  targetNodeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  visited = new Set<string>()
): string[] {
  if (visited.has(targetNodeId)) return [];
  visited.add(targetNodeId);

  const upstreamIds = edges
    .filter((e) => e.targetNodeId === targetNodeId)
    .map((e) => e.sourceNodeId);

  const result: string[] = [];
  for (const id of upstreamIds) {
    result.push(...collectUpstreamChain(id, nodes, edges, visited));
    if (!result.includes(id)) {
      result.push(id);
    }
  }
  return result;
}

/**
 * 对给定节点 ID 列表做拓扑排序（Kahn 算法，从上游到下游）
 */
export function topologicalSort(
  nodeIds: string[],
  edges: CanvasEdge[]
): string[] {
  const inDegree = new Map<string, number>();
  const adjList = new Map<string, string[]>();

  for (const id of nodeIds) {
    inDegree.set(id, 0);
    adjList.set(id, []);
  }

  for (const edge of edges) {
    if (nodeIds.includes(edge.sourceNodeId) && nodeIds.includes(edge.targetNodeId)) {
      inDegree.set(edge.targetNodeId, (inDegree.get(edge.targetNodeId) || 0) + 1);
      const list = adjList.get(edge.sourceNodeId) || [];
      list.push(edge.targetNodeId);
      adjList.set(edge.sourceNodeId, list);
    }
  }

  const queue: string[] = [];
  for (const [id, degree] of inDegree) {
    if (degree === 0) queue.push(id);
  }

  const sorted: string[] = [];
  while (queue.length > 0) {
    const current = queue.shift()!;
    sorted.push(current);
    for (const neighbor of adjList.get(current) || []) {
      const nextDegree = (inDegree.get(neighbor) || 0) - 1;
      inDegree.set(neighbor, nextDegree);
      if (nextDegree === 0) queue.push(neighbor);
    }
  }

  return sorted;
}

/**
 * 判断节点是否需要执行：
 * - LLM 节点：如果已有 output 则跳过（避免重复执行）
 * - generate_image / generate_video：总是执行
 * - 其他节点（text/image/video 等）：不需要执行，只是数据载体
 */
function needsExecution(node: CanvasNode): boolean {
  if (node.type === 'llm') {
    const output = (node.data as { output?: string }).output;
    return !output || !output.trim();
  }
  if (node.type === 'generate_image' || node.type === 'generate_video') {
    return true;
  }
  return false;
}

/** 单节点执行结果：skipped（已删除 / 无需执行 / 缺 prompt）、failed 时携带原始错误 */
type ExecuteNodeResult =
  | { status: 'executed' }
  | { status: 'skipped' }
  | { status: 'failed'; error: unknown };

/**
 * 执行单个可执行节点（链路执行与全画布执行共用）：
 * 标记执行中 → 按 LLM / 生图 / 生视频分发 → 清除执行标记。
 * 失败不上抛（仅 console.error），由调用方决定中断策略。
 */
async function executeNodeInChain(nodeId: string): Promise<ExecuteNodeResult> {
  // 每次执行重新取最新状态：上游 LLM 刚生成的输出必须被下游节点聚合到，
  // 用旧快照会导致「LLM → 生图」链拿不到上游产出
  const { nodes } = getBoardState();
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) return { status: 'skipped' };
  if (!needsExecution(node)) return { status: 'skipped' };

  // 标记为执行中
  boardActions.setExecutingNodeIds([...getBoardState().executingNodeIds, nodeId]);

  try {
    if (node.type === 'llm') {
      await runLlmGeneration(nodeId);
      return { status: 'executed' };
    }
    if (node.type === 'generate_image' || node.type === 'generate_video') {
      // 上游链路执行中（其他链路/用户单独触发的上游）：等待完成后再聚合，
      // 并重读最新图状态（等待期间上游输出已落图）
      await waitForUpstreamIdle(nodeId);
      const fresh = getBoardState();
      const freshNode = fresh.nodes.find((n) => n.id === nodeId);
      if (!freshNode) return { status: 'skipped' };
      const typedNode = freshNode as Parameters<typeof aggregateUpstream>[0];
      const upstream = aggregateUpstream(typedNode, fresh.nodes, fresh.edges);
      if (!upstream.prompt) {
        console.warn(`[chain] ${node.type === 'generate_image' ? '生图' : '生视频'}节点 ${nodeId} 缺少 prompt，跳过`);
        return { status: 'skipped' };
      }
      if (node.type === 'generate_image') {
        await runImageGeneration({
          generateNode: typedNode as Parameters<typeof runImageGeneration>[0]['generateNode'],
          prompt: upstream.prompt,
          referenceImages: upstream.referenceImages,
          maskImage: (freshNode.data as { maskImage?: string }).maskImage,
        });
      } else {
        // 参考视频：仅当生视频节点当前模型+模式支持时消费（画布视频消费的唯一例外）
        const videoData = freshNode.data as GenerateVideoNodeData;
        const videoMode = (videoData.mode || 'text2video') as Parameters<
          typeof modelSupportsVideoReference
        >[1];
        const referenceVideoRefs =
          videoData.modelId && modelSupportsVideoReference(videoData.modelId, videoMode)
            ? aggregateUpstreamVideoRefs(nodeId, fresh.nodes, fresh.edges)
            : undefined;
        await runVideoGeneration({
          generateNode: typedNode as Parameters<typeof runVideoGeneration>[0]['generateNode'],
          prompt: upstream.prompt,
          referenceImages: upstream.referenceImages,
          firstFrameImage: upstream.firstFrameImage,
          lastFrameImage: upstream.lastFrameImage,
          ...(referenceVideoRefs && referenceVideoRefs.length > 0
            ? {
                referenceVideoUrls: referenceVideoRefs.map((ref) => ref.url),
                referenceVideoDurations: referenceVideoRefs.map((ref) => ref.durationSec),
              }
            : {}),
        });
      }
      return { status: 'executed' };
    }
    return { status: 'skipped' };
  } catch (err) {
    console.error(`[chain] 节点 ${nodeId} 执行失败:`, err);
    return { status: 'failed', error: err };
  } finally {
    boardActions.setExecutingNodeIds(getBoardState().executingNodeIds.filter((id) => id !== nodeId));
  }
}

/**
 * 执行单条链路（从目标节点向上游回溯，按拓扑顺序执行）。
 * 任一节点真实失败时中断链路并抛出原始错误。
 */
export async function executeChain(targetNodeId: string): Promise<void> {
  // 并发防护：同一目标节点的链路正在执行时直接跳过，避免重复触发导致重复扣积分
  if (getBoardState().executingNodeIds.includes(targetNodeId)) return;

  const { nodes, edges } = getBoardState();

  // 1. 收集所有上游节点 + 目标节点
  const upstreamIds = collectUpstreamChain(targetNodeId, nodes, edges);
  const allNodeIds = [...upstreamIds, targetNodeId];

  // 2. 拓扑排序（从上游到下游）
  const sortedIds = topologicalSort(allNodeIds, edges);

  // 3. 依次执行
  for (const nodeId of sortedIds) {
    const result = await executeNodeInChain(nodeId);
    // 链路语义：失败即中断，原始错误上抛（调用方负责 toast）
    if (result.status === 'failed') throw result.error;
  }
}

/** 全画布执行汇总 */
export interface ExecuteAllResult {
  /** 成功执行的节点数 */
  executed: number;
  /** 失败的节点数 */
  failed: number;
  /** 失败明细（用于 toast / 排查） */
  failures: Array<{ nodeId: string; nodeType: string; message: string }>;
}

/**
 * 执行全画布：所有 generate_image / generate_video 节点 + 无输出的 LLM 节点。
 *
 * 与单链路执行的差异：
 *  - 范围是整张画布（按全画布拓扑序串行，上游产出可被下游聚合）；
 *  - 单节点失败不中断其他无关分支，结束后返回汇总。
 * 注意：generate 节点总是执行，重跑会替换旧的自动生成子节点（幂等）。
 */
export async function executeAllNodes(): Promise<ExecuteAllResult> {
  const { nodes, edges } = getBoardState();
  const sortedIds = topologicalSort(nodes.map((n) => n.id), edges);

  const result: ExecuteAllResult = { executed: 0, failed: 0, failures: [] };
  for (const nodeId of sortedIds) {
    const r = await executeNodeInChain(nodeId);
    if (r.status === 'executed') {
      result.executed++;
    } else if (r.status === 'failed') {
      result.failed++;
      result.failures.push({
        nodeId,
        nodeType: getBoardState().nodes.find((n) => n.id === nodeId)?.type ?? 'unknown',
        message: r.error instanceof Error ? r.error.message : String(r.error ?? '未知错误'),
      });
    }
  }
  return result;
}
